import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import API_CONFIG from '../config/api.config';
import AuthService from '../services/api/auth.service';

// API functions separated from hooks for better testability and reuse
export const componentApi = {
  getComponents: async () => {
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_COMPONENTS}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${AuthService.getAccessToken()}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch components data');
    }

    const data = await response.json();

    if (!data.components) {
      throw new Error('Invalid response format');
    }

    // Transform the data for the SearchableDropdown format
    return data.components.map(component => ({
      value: component.id.toString(),
      label: component.name,
      secondaryText: component.name_en !== component.name ? component.name_en : null,
      tags: component.tags,
      filters: component.filters
    }));
  },

  getComponentAssociations: async () => {
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_COMPONENT_ASSOCIATIONS}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${AuthService.getAccessToken()}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch component associations');
    }

    return response.json();
  },

  getComponentCategories: async () => {
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_COMPONENT_CATEGORIES}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${AuthService.getAccessToken()}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      // Attempt to parse error details from the response
      let errorDetails = 'Failed to fetch component categories';
      try {
        const errorData = await response.json();
        if (errorData?.detail?.message) {
          errorDetails = errorData.detail.message;
        } else if (errorData?.detail) {
          errorDetails = JSON.stringify(errorData.detail);
        }
      } catch (e) {
        // Ignore if response is not JSON
      }
      throw new Error(errorDetails);
    }

    const data = await response.json();
    
    // Check if data has the expected format (component_categories array)
    if (data.component_categories && Array.isArray(data.component_categories)) {
      console.log('Using component_categories array from response');
      return data.component_categories;
    }
    
    // Check if data itself is an array
    if (Array.isArray(data)) {
      console.log('Response is already an array');
      return data;
    }
    
    console.warn('Unexpected response format for component categories:', data);
    return []; // Return empty array if format is unexpected
  }
};

// React Query hooks
export function useComponents(options = {}) {
  return useQuery({
    queryKey: ['components'],
    queryFn: componentApi.getComponents,
    ...options
  });
}

export function useComponentAssociations(options = {}) {
  return useQuery({
    queryKey: ['componentAssociations'],
    queryFn: componentApi.getComponentAssociations,
    ...options
  });
}

// New hook for component categories
export function useComponentCategories(options = {}) {
  return useQuery({
    queryKey: ['componentCategories'],
    queryFn: componentApi.getComponentCategories,
    ...options
  });
}

// Helper function to manually invalidate cache
export function useInvalidateComponentQueries() {
  const queryClient = useQueryClient();
  
  return {
    invalidateComponents: () => queryClient.invalidateQueries({ queryKey: ['components'] }),
    invalidateComponentAssociations: () => queryClient.invalidateQueries({ queryKey: ['componentAssociations'] }),
    invalidateComponentCategories: () => queryClient.invalidateQueries({ queryKey: ['componentCategories'] }),
    invalidateAll: () => queryClient.invalidateQueries({ 
      queryKey: [{ prefix: 'components' }, { prefix: 'componentAssociations' }, { prefix: 'componentCategories' }] 
    })
  };
} 